import React, { useState } from 'react';

import styled from 'styled-components';

import { bulkUpdateCommunityPreferences } from 'apis/CommunityAPI';
import { useRootContext } from 'components/layout/RootContext';
import { COMMUNITY_SETTINGS_PAGE_TYPE } from 'components/Settings/constants';
import SettingsPage from 'components/Settings/SettingsPage';
import { CommunityPreferences, CommunityPreferenceType } from 'types/communities';

import SpreadsheetExportModal from './SpreadsheetExportModal';

const ColorCircle = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  border: 1px solid var(--light-gray);
`;

const NoPreferencesLabel = styled.div`
  color: var(--text-secondary);
  font-size: 14px;
`;

const Container = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 10px;
`;

const getItemStatusComponent = (communityPreference: CommunityPreferenceType) => {
  const { preferences } = communityPreference;
  if (preferences?.spreadsheet_export_color) {
    return (
      <Container>
        <ColorCircle color={preferences.spreadsheet_export_color} />
      </Container>
    );
  }

  return <NoPreferencesLabel>No preferences</NoPreferencesLabel>;
};

const SpreadsheetExport = () => {
  const { showMessage } = useRootContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [communitiesToModify, setCommunitiesToModify] = useState<CommunityPreferenceType[]>([]);
  const [refetchCommunities, setRefetchCommunities] = useState<() => void | undefined>();
  const [clearSelection, setClearSelection] = useState<() => void | undefined>();

  const showCommunitiesEditModal = (items: any[]) => {
    setCommunitiesToModify(items);
    setIsModalOpen(true);
  };

  const saveSpreadsheetExportSettings = async (newColor: string) => {
    setLoading(true);
    try {
      const payload: CommunityPreferences[] = communitiesToModify.map((community) => ({
        community: community.id,
        spreadsheet_export_color: newColor,
      }));

      await bulkUpdateCommunityPreferences(payload);
      setLoading(false);
      showMessage('success', 'All selected communities updated successfully');

      if (refetchCommunities) refetchCommunities();
      if (clearSelection) clearSelection();
    } catch (error) {
      setLoading(false);
      showMessage('error', 'Failed to update one or more communities!');
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <SettingsPage
        hideFilters
        settingsPageType={COMMUNITY_SETTINGS_PAGE_TYPE.SPREADSHEET_EXPORT}
        getItemStatusComponent={getItemStatusComponent}
        showCommunitiesEditModal={showCommunitiesEditModal}
        setRefetchCommunities={setRefetchCommunities}
        setClearSelection={setClearSelection}
      />
      {isModalOpen && (
        <SpreadsheetExportModal
          isOpen={isModalOpen}
          loading={loading}
          communitiesToModify={communitiesToModify}
          onSave={saveSpreadsheetExportSettings}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default SpreadsheetExport;
